export const IMPERSONATION_ADMIN_TOKEN_KEY = "adminToken";
export const IMPERSONATION_RETURN_PATH_KEY = "impersonationReturnPath";
export const AUTH_TRANSITION_STORAGE_KEY = "lunovaAuthTransition";
export const AUTH_TRANSITION_SESSION_KEY = "lunovaAuthTransition";
export const AUTH_TRANSITION_TTL_MS = 30000;

export const DEFAULT_ADMIN_RETURN_PATH = "/dashboard";
export const IMPERSONATION_BLOCKED_MESSAGE =
  "Return to Admin before making support changes while impersonating a customer.";

const AUTH_PATH_PREFIXES = [
  "/login",
  "/register",
  "/reset-password",
  "/forgot-password",
];

const getStorage = () => {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage || null;
  } catch (error) {
    return null;
  }
};

const getSessionStorage = () => {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage || null;
  } catch (error) {
    return null;
  }
};

const writeAuthTransitionTimestamp = () => {
  const timestamp = String(Date.now());
  const localStorageRef = getStorage();
  const sessionStorageRef = getSessionStorage();

  if (localStorageRef) {
    localStorageRef.setItem(AUTH_TRANSITION_STORAGE_KEY, timestamp);
  }

  if (sessionStorageRef) {
    sessionStorageRef.setItem(AUTH_TRANSITION_SESSION_KEY, timestamp);
  }
};

export const beginAuthTransition = () => {
  writeAuthTransitionTimestamp();
};

export const endAuthTransition = () => {
  const localStorageRef = getStorage();
  const sessionStorageRef = getSessionStorage();

  if (localStorageRef) {
    localStorageRef.removeItem(AUTH_TRANSITION_STORAGE_KEY);
  }

  if (sessionStorageRef) {
    sessionStorageRef.removeItem(AUTH_TRANSITION_SESSION_KEY);
  }
};

const readAuthTransitionTimestamp = () => {
  const localStorageRef = getStorage();
  const sessionStorageRef = getSessionStorage();

  return (
    localStorageRef?.getItem(AUTH_TRANSITION_STORAGE_KEY) ||
    sessionStorageRef?.getItem(AUTH_TRANSITION_SESSION_KEY) ||
    null
  );
};

export const isAuthTransitionInProgress = () => {
  const raw = readAuthTransitionTimestamp();

  if (!raw) return false;

  const startedAt = Number(raw);

  if (!Number.isFinite(startedAt)) {
    return true;
  }

  if (Date.now() - startedAt >= AUTH_TRANSITION_TTL_MS) {
    endAuthTransition();

    return false;
  }

  return true;
};

export const decodeDashboardToken = (token) => {
  if (typeof token !== "string") return null;

  const [, payload] = token.split(".");

  if (!payload || typeof atob !== "function") return null;

  try {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedBase64 = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );
    const jsonPayload = decodeURIComponent(
      atob(paddedBase64)
        .split("")
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

export const isImpersonationToken = (token) =>
  decodeDashboardToken(token)?.isImpersonation === true;

const isExpiredToken = (decodedToken, clockSkewMs = 5000) =>
  !!decodedToken?.exp && decodedToken.exp * 1000 <= Date.now() + clockSkewMs;

export const isAdminDashboardToken = (token) => {
  const decodedToken = decodeDashboardToken(token);

  return (
    decodedToken?.role === "admin" &&
    decodedToken?.type === "dashboard" &&
    !!decodedToken?.adminId &&
    !isExpiredToken(decodedToken)
  );
};

const normalizeFallback = (fallback) =>
  typeof fallback === "string" &&
  fallback.startsWith("/") &&
  !fallback.startsWith("//")
    ? fallback
    : DEFAULT_ADMIN_RETURN_PATH;

export const getCurrentLocationPath = (
  location = typeof window !== "undefined" ? window.location : null
) => {
  if (!location) return DEFAULT_ADMIN_RETURN_PATH;

  return `${location.pathname || DEFAULT_ADMIN_RETURN_PATH}${
    location.search || ""
  }${location.hash || ""}`;
};

export const buildCustomerDetailsReturnPath = (customerId) =>
  customerId
    ? `/customers/${encodeURIComponent(customerId)}`
    : DEFAULT_ADMIN_RETURN_PATH;

export const sanitizeAdminReturnPath = (
  path,
  fallback = DEFAULT_ADMIN_RETURN_PATH
) => {
  const safeFallback = normalizeFallback(fallback);

  if (typeof path !== "string") return safeFallback;

  const trimmed = path.trim();

  if (!trimmed || !trimmed.startsWith("/") || trimmed.startsWith("//")) {
    return safeFallback;
  }

  let normalized;
  let pathname;

  try {
    const url = new URL(trimmed, "https://dashboard.lunova.local");

    ({ pathname } = url);
    normalized = `${url.pathname}${url.search}${url.hash}`;
  } catch (error) {
    return safeFallback;
  }

  if (!normalized.startsWith("/") || normalized.startsWith("//")) {
    return safeFallback;
  }

  const isAuthPath = AUTH_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

  return isAuthPath ? safeFallback : normalized;
};

export const storeAdminReturnPath = (
  path = getCurrentLocationPath(),
  fallback = DEFAULT_ADMIN_RETURN_PATH
) => {
  const safePath = sanitizeAdminReturnPath(path, fallback);
  const storage = getStorage();

  if (storage) {
    storage.setItem(IMPERSONATION_RETURN_PATH_KEY, safePath);
  }

  return safePath;
};

export const getStoredAdminReturnPath = ({
  customerId,
  fallback = DEFAULT_ADMIN_RETURN_PATH,
} = {}) => {
  const customerFallback = customerId
    ? buildCustomerDetailsReturnPath(customerId)
    : fallback;
  const storage = getStorage();
  const storedPath = storage?.getItem(IMPERSONATION_RETURN_PATH_KEY);

  return sanitizeAdminReturnPath(storedPath, customerFallback);
};

export const clearAdminReturnPath = () => {
  const storage = getStorage();

  if (storage) {
    storage.removeItem(IMPERSONATION_RETURN_PATH_KEY);
  }
};

export const getStoredAdminToken = () => {
  const storage = getStorage();

  return storage?.getItem(IMPERSONATION_ADMIN_TOKEN_KEY) || null;
};

export const restoreStoredAdminSession = ({
  fallback = DEFAULT_ADMIN_RETURN_PATH,
} = {}) => {
  const storage = getStorage();
  const adminToken = getStoredAdminToken();

  if (!storage || !isAdminDashboardToken(adminToken)) return null;

  const returnPath = getStoredAdminReturnPath({ fallback });

  storage.setItem("authToken", adminToken);
  storage.removeItem(IMPERSONATION_ADMIN_TOKEN_KEY);
  storage.removeItem(IMPERSONATION_RETURN_PATH_KEY);

  return {
    adminToken,
    returnPath,
  };
};

export const clearStoredImpersonation = () => {
  const storage = getStorage();

  if (storage) {
    storage.removeItem(IMPERSONATION_ADMIN_TOKEN_KEY);
    storage.removeItem(IMPERSONATION_RETURN_PATH_KEY);
  }
};

export const isImpersonationActive = () => {
  const storage = getStorage();

  return (
    !!storage?.getItem(IMPERSONATION_ADMIN_TOKEN_KEY) ||
    isImpersonationToken(storage?.getItem("authToken"))
  );
};

export const shouldDeferUnauthorizedRecovery = () => {
  if (isAuthTransitionInProgress()) return true;

  const storage = getStorage();

  if (!storage) return false;

  const authToken = storage.getItem("authToken");
  const parkedAdminToken = storage.getItem(IMPERSONATION_ADMIN_TOKEN_KEY);

  return (
    isImpersonationToken(authToken) && isAdminDashboardToken(parkedAdminToken)
  );
};

export const assertNotImpersonatingForSupportAction = (
  message = IMPERSONATION_BLOCKED_MESSAGE
) => {
  if (isImpersonationActive()) {
    throw new Error(message);
  }
};
